import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { loadPhotoAsset, revokePhotoAsset } from '../lib/image';
import { BUILT_IN_PRESETS, cloneSettings, DEFAULT_SETTINGS } from '../lib/presets';
import { canvasToBlob, exportFileName, renderForExport } from '../lib/render';
import { useLanguage } from '../i18n';
import type { ExportSettings, FramePreset, FrameSettings, FrameSettingsPatch, PhotoAsset } from '../types';

const CUSTOM_PRESETS_KEY = 'lumaframe.customPresets.v1';
const SETTINGS_KEY = 'lumaframe.lastSettings.v1';
const THEME_KEY = 'lumaframe.theme.v1';

interface PhotoContextValue {
  activePhoto: PhotoAsset | null;
  batchPhotos: PhotoAsset[];
  settings: FrameSettings;
  exportSettings: ExportSettings;
  presets: FramePreset[];
  isLoading: boolean;
  error: string | null;
  notice: string | null;
  darkMode: boolean;
  canUndo: boolean;
  canRedo: boolean;
  loadFiles: (files: FileList | File[]) => Promise<void>;
  resetPhoto: () => void;
  updateSettings: (patch: FrameSettingsPatch) => void;
  replaceSettings: (next: FrameSettings) => void;
  updateExportSettings: (patch: Partial<ExportSettings>) => void;
  applyPreset: (preset: FramePreset) => void;
  saveCurrentPreset: (name: string) => void;
  importPresets: (json: string) => void;
  exportPresets: () => void;
  downloadActive: () => Promise<void>;
  copyActive: () => Promise<void>;
  downloadBatch: () => Promise<void>;
  undo: () => void;
  redo: () => void;
  toggleDarkMode: () => void;
  clearNotice: () => void;
}

const PhotoContext = createContext<PhotoContextValue | null>(null);

const DEFAULT_EXPORT_SETTINGS: ExportSettings = {
  format: 'image/jpeg',
  quality: 92,
  sizeMode: 'original',
  presetLongEdge: 2048,
  customWidth: 2048,
  customHeight: 2048,
  fileName: ''
};

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function nextSettings(current: FrameSettings, patch: FrameSettingsPatch): FrameSettings {
  return {
    ...current,
    ...patch,
    shadow: patch.shadow ? { ...current.shadow, ...patch.shadow } : current.shadow,
    corners: patch.corners ? { ...current.corners, ...patch.corners } : current.corners,
    metadata: patch.metadata ? { ...current.metadata, ...patch.metadata } : current.metadata
  };
}

export function PhotoProvider({ children }: { children: ReactNode }): JSX.Element {
  const { t } = useLanguage();
  const [activePhoto, setActivePhoto] = useState<PhotoAsset | null>(null);
  const [batchPhotos, setBatchPhotos] = useState<PhotoAsset[]>([]);
  const [settings, setSettings] = useState<FrameSettings>(() => readJson(SETTINGS_KEY, DEFAULT_SETTINGS));
  const [exportSettings, setExportSettings] = useState<ExportSettings>(DEFAULT_EXPORT_SETTINGS);
  const [customPresets, setCustomPresets] = useState<FramePreset[]>(() => readJson(CUSTOM_PRESETS_KEY, []));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [darkMode, setDarkMode] = useState(() => readJson(THEME_KEY, false));
  const [past, setPast] = useState<FrameSettings[]>([]);
  const [future, setFuture] = useState<FrameSettings[]>([]);

  const presets = useMemo(() => [...BUILT_IN_PRESETS, ...customPresets], [customPresets]);

  const errorText = useCallback(
    (err: unknown, fallbackKey: string) => {
      if (err instanceof Error && err.message.startsWith('error.')) return t(err.message);
      return t(fallbackKey);
    },
    [t]
  );

  useEffect(() => {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  }, [settings]);

  useEffect(() => {
    localStorage.setItem(CUSTOM_PRESETS_KEY, JSON.stringify(customPresets));
  }, [customPresets]);

  useEffect(() => {
    localStorage.setItem(THEME_KEY, JSON.stringify(darkMode));
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  useEffect(() => {
    return () => {
      revokePhotoAsset(activePhoto);
      batchPhotos.forEach(revokePhotoAsset);
    };
  }, [activePhoto, batchPhotos]);

  const pushSettings = useCallback((next: FrameSettings) => {
    setSettings((current) => {
      setPast((items) => [...items.slice(-29), cloneSettings(current)]);
      setFuture([]);
      return next;
    });
  }, []);

  const loadFiles = useCallback(async (files: FileList | File[]) => {
    const incoming = Array.from(files).filter((file) => file.type.startsWith('image/') || /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name));
    if (!incoming.length) {
      setError(t('error.chooseImage'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const assets = await Promise.all(incoming.map(loadPhotoAsset));
      setActivePhoto((current) => {
        revokePhotoAsset(current);
        return assets[0];
      });
      setBatchPhotos((current) => {
        current.forEach(revokePhotoAsset);
        return assets;
      });
      setExportSettings((current) => ({ ...current, fileName: '' }));
      window.history.pushState({}, '', '/editor');
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch (err) {
      setError(errorText(err, 'error.loadPhoto'));
    } finally {
      setIsLoading(false);
    }
  }, [errorText, t]);

  const resetPhoto = useCallback(() => {
    setActivePhoto((current) => {
      revokePhotoAsset(current);
      return null;
    });
    setBatchPhotos((current) => {
      current.forEach(revokePhotoAsset);
      return [];
    });
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  const updateSettings = useCallback(
    (patch: FrameSettingsPatch) => {
      pushSettings(nextSettings(settings, patch));
    },
    [pushSettings, settings]
  );

  const replaceSettings = useCallback(
    (next: FrameSettings) => {
      pushSettings(cloneSettings(next));
    },
    [pushSettings]
  );

  const updateExportSettings = useCallback((patch: Partial<ExportSettings>) => {
    setExportSettings((current) => ({ ...current, ...patch }));
  }, []);

  const applyPreset = useCallback(
    (preset: FramePreset) => {
      replaceSettings(preset.settings);
      setNotice(t('notice.appliedPreset', { name: preset.name }));
    },
    [replaceSettings, t]
  );

  const saveCurrentPreset = useCallback(
    (name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      setCustomPresets((items) => [
        ...items,
        {
          id: `custom-${crypto.randomUUID()}`,
          name: trimmed,
          description: t('preset.local'),
          settings: cloneSettings(settings)
        }
      ]);
      setNotice(t('notice.savedPreset', { name: trimmed }));
    },
    [settings, t]
  );

  const importPresets = useCallback((json: string) => {
    try {
      const parsed = JSON.parse(json) as FramePreset[];
      if (!Array.isArray(parsed)) throw new Error('error.presetArray');
      setCustomPresets((items) => [...items, ...parsed.map((preset) => ({ ...preset, id: `imported-${crypto.randomUUID()}`, builtIn: false }))]);
      setNotice(t('notice.importedPresets', { count: parsed.length }));
    } catch (err) {
      setError(errorText(err, 'error.importPresets'));
    }
  }, [errorText, t]);

  const exportPresets = useCallback(() => {
    const blob = new Blob([JSON.stringify(customPresets, null, 2)], { type: 'application/json' });
    downloadBlob(blob, 'lumaframe-presets.json');
  }, [customPresets]);

  const downloadActive = useCallback(async () => {
    if (!activePhoto) return;
    try {
      const canvas = renderForExport(activePhoto, settings, exportSettings);
      const blob = await canvasToBlob(canvas, exportSettings);
      downloadBlob(blob, exportFileName(activePhoto, exportSettings));
      setNotice(t('notice.exportReady'));
    } catch (err) {
      setError(errorText(err, 'error.exportImage'));
    }
  }, [activePhoto, errorText, exportSettings, settings, t]);

  const copyActive = useCallback(async () => {
    if (!activePhoto) return;
    try {
      const canvas = renderForExport(activePhoto, settings, { ...exportSettings, format: 'image/png' });
      const blob = await canvasToBlob(canvas, { ...exportSettings, format: 'image/png' });
      if (!navigator.clipboard || typeof ClipboardItem === 'undefined') {
        throw new Error('error.copyUnsupported');
      }
      await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
      setNotice(t('notice.copied'));
    } catch (err) {
      setError(errorText(err, 'error.copyImage'));
    }
  }, [activePhoto, errorText, exportSettings, settings, t]);

  const downloadBatch = useCallback(async () => {
    if (!batchPhotos.length) return;
    try {
      for (const asset of batchPhotos) {
        const canvas = renderForExport(asset, settings, { ...exportSettings, fileName: '' });
        const blob = await canvasToBlob(canvas, exportSettings);
        downloadBlob(blob, exportFileName(asset, { ...exportSettings, fileName: '' }));
        await new Promise((resolve) => window.setTimeout(resolve, 160));
      }
      setNotice(t('notice.batchExported', { count: batchPhotos.length }));
    } catch (err) {
      setError(errorText(err, 'error.batchExport'));
    }
  }, [batchPhotos, errorText, exportSettings, settings, t]);

  const undo = useCallback(() => {
    setPast((items) => {
      const previous = items[items.length - 1];
      if (!previous) return items;
      setFuture((futureItems) => [cloneSettings(settings), ...futureItems.slice(0, 29)]);
      setSettings(previous);
      return items.slice(0, -1);
    });
  }, [settings]);

  const redo = useCallback(() => {
    setFuture((items) => {
      const next = items[0];
      if (!next) return items;
      setPast((pastItems) => [...pastItems.slice(-29), cloneSettings(settings)]);
      setSettings(next);
      return items.slice(1);
    });
  }, [settings]);

  const value = useMemo<PhotoContextValue>(
    () => ({
      activePhoto,
      batchPhotos,
      settings,
      exportSettings,
      presets,
      isLoading,
      error,
      notice,
      darkMode,
      canUndo: past.length > 0,
      canRedo: future.length > 0,
      loadFiles,
      resetPhoto,
      updateSettings,
      replaceSettings,
      updateExportSettings,
      applyPreset,
      saveCurrentPreset,
      importPresets,
      exportPresets,
      downloadActive,
      copyActive,
      downloadBatch,
      undo,
      redo,
      toggleDarkMode: () => setDarkMode((value) => !value),
      clearNotice: () => {
        setNotice(null);
        setError(null);
      }
    }),
    [
      activePhoto,
      applyPreset,
      batchPhotos,
      copyActive,
      customPresets,
      darkMode,
      downloadActive,
      downloadBatch,
      error,
      exportPresets,
      exportSettings,
      future.length,
      importPresets,
      isLoading,
      loadFiles,
      notice,
      past.length,
      presets,
      replaceSettings,
      resetPhoto,
      saveCurrentPreset,
      settings,
      undo,
      redo,
      updateExportSettings,
      updateSettings
    ]
  );

  return <PhotoContext.Provider value={value}>{children}</PhotoContext.Provider>;
}

export function usePhoto(): PhotoContextValue {
  const context = useContext(PhotoContext);
  if (!context) throw new Error('usePhoto must be used inside PhotoProvider.');
  return context;
}
